import { useEffect, useState } from 'react';
import {
    IsometricBuilding,
    DetectionLayer,
    SuppressionLayer,
    NetworkLayer,
} from './BuildingLayers';

/**
 * Building Blueprint Hero Component
 * Scroll-driven 3D building visualization with state transformations
 * Phase 4 enhancement inspired by anime.js
 */
export function BuildingBlueprintHero() {
    const [scrollProgress, setScrollProgress] = useState(0);

    useEffect(() => {
        const updateScrollProgress = () => {
            const maxScroll =
                document.documentElement.scrollHeight - window.innerHeight;
            const progress = Math.min(1, Math.max(0, window.scrollY / maxScroll));
            setScrollProgress(progress);
        };

        window.addEventListener('scroll', updateScrollProgress, { passive: true });
        updateScrollProgress(); // Initial calculation

        return () => window.removeEventListener('scroll', updateScrollProgress);
    }, []);

    // Map scroll zones to layer visibility
    const buildingState = {
        scale: 1 + scrollProgress * 0.2, // Scale up slightly as user scrolls
        rotate: scrollProgress * 10, // Gentle rotation
        opacity: Math.max(0.3, 1 - scrollProgress * 1.5), // Fade out as user scrolls down
        layers: {
            detection: scrollProgress > 0.1 && scrollProgress < 0.3,
            suppression: scrollProgress > 0.3 && scrollProgress < 0.5,
            network: scrollProgress > 0.5 && scrollProgress < 0.7,
        },
    };

    return (
        <div
            className="fixed inset-0 pointer-events-none flex items-center justify-center"
            style={{ zIndex: 0 }}
            aria-hidden="true"
        >
            <svg
                viewBox="0 0 800 600"
                className="w-full h-full max-w-2xl transition-transform duration-100"
                style={{
                    transform: `scale(${buildingState.scale}) rotate(${buildingState.rotate}deg)`,
                    opacity: buildingState.opacity,
                }}
            >
                {/* Base building wireframe */}
                <IsometricBuilding />

                {/* Detection layer (visible 10-30% scroll) */}
                <DetectionLayer animate={buildingState.layers.detection} />

                {/* Suppression layer (visible 30-50% scroll) */}
                <SuppressionLayer animate={buildingState.layers.suppression} />

                {/* Network layer (visible 50-70% scroll) */}
                <NetworkLayer animate={buildingState.layers.network} />
            </svg>
        </div>
    );
}

export default BuildingBlueprintHero;
